import { faPlayCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import TimeOfUsable from 'src/components/TradeRule/TimeOfUse/TimeOfUsable';
import { APIConfig } from 'src/config';
import {
  PLATFORM_MODE_REBATE, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_RULE_STATE_PROPOSED,
} from 'src/util/constants';
import { convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';
import getUserDisplayName from 'src/util/i18n/decorators/getUserDisplayName';
import { i18nDecimalFormat } from 'src/util/i18n/handler';
import { buildTradePointInlineLabel } from 'src/util/i18n/tradePoint';
import { hasSharedConditions as hasSharedTimeOfUseConditions } from 'src/util/timeOfUse';
import { getPriceRange } from 'src/util/tradeRule';

import { buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';
import TradeRuleTimelineStep from './TradeRuleTimelineStep';
import { getTradeData } from '../helpers';

/**
 * Generates the trade rule(buy) price information for a given trade rule
 * @param {object} root0
 * @param {number} root0.maximum
 * @param {number} root0.minimum
 * @param {'PLATFORM_MODE_REBATE'|'PLATFORM_TRADE'} root0.mode
 * @param {React.ReactElement} root0.units
 * @returns {React.ReactComponentElement} - i18n component that carries the (buy) pricing info
 */
export const buyPriceInformation = ({
  maximum, minimum, mode, units,
}) => {
  const tradeDirectionBuy = (
    <FormattedMessage
      id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy.label"
      defaultMessage="Buying"
    />
  );
  const intlCurrency = useIntlCurrency();

  const convertPrice = (price) => convertEnergyPricePowertracerToPartner(intlCurrency, price);

  if (mode === PLATFORM_MODE_REBATE && minimum === maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy.trade_rule_price.flat.platform_mode_rebate"
        defaultMessage="{tradeDirection} at a discount of {price} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          price: <strong>{i18nDecimalFormat(convertPrice(minimum)).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionBuy}</strong>,
          units,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum === maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy.trade_rule_price.flat.platform_mode_trade"
        defaultMessage="{tradeDirection} at a price of {price} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          price: <strong>{i18nDecimalFormat(convertPrice(minimum)).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionBuy}</strong>,
          units,
        }}
      />
    );
  }

  if (mode === PLATFORM_MODE_REBATE && minimum !== maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy.trade_rule_price.variable.platform_mode_rebate"
        defaultMessage="{tradeDirection} at a discount of {minimum} to {maximum} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          maximum: <strong>{convertPrice(maximum).toFixed(3)}</strong>,
          minimum: <strong>{convertPrice(minimum).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionBuy}</strong>,
          units,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum !== maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy.trade_rule_price.variable.platform_mode_trade"
        defaultMessage="{tradeDirection} at a price of {minimum} to {maximum} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          maximum: <strong>{convertPrice(maximum).toFixed(3)}</strong>,
          minimum: <strong>{convertPrice(minimum).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionBuy}</strong>,
          units,
        }}
      />
    );
  }

  return null;
};

/**
 * Generates the trade rule(sell) price information for a given trade rule
 * @param {object} root0
 * @param {number} root0.maximum
 * @param {number} root0.minimum
 * @param {'PLATFORM_MODE_REBATE'|'PLATFORM_TRADE'} root0.mode
 * @param {React.ReactElement} root0.units
 * @returns {React.ReactComponentElement} - i18n component that carries the (sell) pricing info
 */
export const sellPriceInformation = ({
  maximum, minimum, mode, units,
}) => {
  const tradeDirectionSell = (
    <FormattedMessage
      id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell.label"
      defaultMessage="Selling"
    />
  );
  const intlCurrency = useIntlCurrency();
  const convertPrice = (price) => convertEnergyPricePowertracerToPartner(intlCurrency, price);

  if (mode === PLATFORM_MODE_REBATE && minimum === maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell.trade_rule_price.flat.platform_mode_rebate"
        defaultMessage="{tradeDirection} at a discount of {price} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          price: <strong>{convertPrice(minimum).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionSell}</strong>,
          units,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum === maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell.trade_rule_price.flat.platform_mode_trade"
        defaultMessage="{tradeDirection} at a price of {price} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          price: <strong>{convertPrice(minimum).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionSell}</strong>,
          units,
        }}
      />
    );
  }

  if (mode === PLATFORM_MODE_REBATE && minimum !== maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell.trade_rule_price.variable.platform_mode_rebate"
        defaultMessage="{tradeDirection} at a discount of {minimum} to {maximum} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          maximum: <strong>{convertPrice(maximum).toFixed(3)}</strong>,
          minimum: <strong>{convertPrice(minimum).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionSell}</strong>,
          units,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum !== maximum) {
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell.trade_rule_price.variable.platform_mode_trade"
        defaultMessage="{tradeDirection} at a price of {minimum} to {maximum} {units}"
        values={{
          // TO DO: Implement d3-format locale support (PT-1124)
          maximum: <strong>{convertPrice(maximum).toFixed(3)}</strong>,
          minimum: <strong>{convertPrice(minimum).toFixed(3)}</strong>,
          tradeDirection: <strong>{tradeDirectionSell}</strong>,
          units,
        }}
      />
    );
  }

  return null;
};
/**
 * Returns the buy proposal details
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {React.ReactElement} tradePointLabel
 * @param {object} counterParty
 * @returns {React.ReactElement} - buy proposal details
 */
const buyTradeDetails = (intl, tradePointLabel, counterParty) => {
  if (counterParty) {
    const counterPartyUser = getUserDisplayName(intl, counterParty);
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy_with_counterparty.details"
        defaultMessage="Buying for {tradePointLabel} from {counterPartyUser}"
        values={{
          tradePointLabel,
          counterPartyUser,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy_no_counterparty.details"
      defaultMessage="Buying for {tradePointLabel}"
      values={{
        tradePointLabel,
      }}
    />
  );
};

/**
 * Returns the sell proposal details
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {React.ReactElement} tradePointLabel
 * @param {string} counterParty
 * @returns {React.ReactElement} - sell proposal details
 */
const sellTradeDetails = (intl, tradePointLabel, counterParty) => {
  if (counterParty) {
    const counterPartyUser = getUserDisplayName(intl, counterParty);
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell_with_counterparty.details"
        defaultMessage="Selling from {tradePointLabel} to {counterPartyUser}"
        values={{
          tradePointLabel,
          counterPartyUser,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell_no_counterparty.details"
      defaultMessage="Selling from {tradePointLabel}"
      values={{
        tradePointLabel,
      }}
    />
  );
};
/**
 * Description
 * @param {any} props
 * @returns {any} - TradeRuleTimelineStepProposed component
 */
function TradeRuleTimelineStepProposed(props) {
  const intl = useIntl();

  const {
    buyer, clauses, hasNextStep, isCurrentStep, ruleId, seller,
    timestamp, timezone, tradeType, user,
  } = props;

  const {
    direction, trader, counterParty,
  } = getTradeData(buyer, seller, user?.id) || {};

  const { user: counterPartyUser } = counterParty || {};

  const mode = APIConfig().MODE;

  const clauseNodes = (!clauses || !clauses.edges)
    ? [] : clauses.edges.map((el) => (el.node)).sort((a, b) => (b.price - a.price));
  const showConditions = hasSharedTimeOfUseConditions(clauseNodes);

  const priceRange = getPriceRange({ clauses }) || {};
  const { maximum, minimum } = priceRange;

  const tradePointLabel = buildTradePointInlineLabel(intl, trader?.tradePoint);

  return (
    <TradeRuleTimelineStep
      hasNextStep={hasNextStep}
      icon={<FontAwesomeIcon icon={faPlayCircle} />}
      isCurrentStep={isCurrentStep}
      ruleId={ruleId}
      state={TRADE_RULE_STATE_PROPOSED}
      tradeType={tradeType}
      timestamp={timestamp}
      timezone={timezone}
      user={user}
    >
      {tradePointLabel && (
        <li className="mb-3">
          {direction === TRADE_DIRECTION_BUY && (
            buyTradeDetails(intl, tradePointLabel, counterPartyUser)
          )}
          {direction === TRADE_DIRECTION_SELL && (
            sellTradeDetails(intl, tradePointLabel, counterPartyUser)
          )}

        </li>
      )}
      <li className="mb-3">
        {direction === TRADE_DIRECTION_BUY && (
          buildTradeRulePriceInformation({
            direction: TRADE_DIRECTION_BUY,
            intl,
            maximum,
            minimum,
            mode,
            timeline: true,
          })
        )}
        {direction === TRADE_DIRECTION_SELL && (
          buildTradeRulePriceInformation({
            direction: TRADE_DIRECTION_SELL,
            intl,
            maximum,
            minimum,
            mode,
            timeline: true,
          })
        )}
      </li>
      <ul>
        {clauseNodes.length > 1 && clauseNodes.map((clause) => (
          <TimeOfUsable
            key={clause.id}
            timeOfUsable={clause}
            showConditions={showConditions}
          />
        ))}
      </ul>
    </TradeRuleTimelineStep>
  );
}

TradeRuleTimelineStepProposed.propTypes = {
  buyer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  clauses: PropTypes.shape({
    edges: PropTypes.arrayOf(PropTypes.shape({
      node: (PropTypes.shape({
        ignoreDaylightSavings: PropTypes.bool.isRequired,
        ignorePublicHolidays: PropTypes.bool.isRequired,
        price: PropTypes.number.isRequired,
        timezone: PropTypes.string.isRequired,
      })),
    })),
  }).isRequired,
  isCurrentStep: PropTypes.bool.isRequired,
  hasNextStep: PropTypes.bool.isRequired,
  ruleId: PropTypes.string.isRequired,
  seller: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  timestamp: PropTypes.number.isRequired,
  timezone: PropTypes.string.isRequired,
  tradeType: PropTypes.string.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
  }).isRequired,
};

export default TradeRuleTimelineStepProposed;
